/**
 * Words and small rules for Settings › Portals (docs/integration-portal-link-proposal.md §4.3 in the
 * SVN repo). Pure, so the page's table and form read the same in the specs as in the browser.
 */
import { parseUtc } from "../settings/sourceTime";

export const PORTAL_NAME_MAX = 60;
export const PORTAL_ICON_MAX = 40;

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** The help under the Address field, naming the sites the server allows. */
export function addressHelp(allowedOrigins) {
  const origins = (Array.isArray(allowedOrigins) ? allowedOrigins : [])
    .map((x) => String(x || "").trim())
    .filter(Boolean);
  const where = origins.length ? origins.join(" or ") : "Inistate's portal site";
  return "Portals are pages Inistate publishes for your workspace on " + where
    + ". Ask your Inistate consultant for the address.";
}

/** The address as the table shows it: without the https:// every portal has. */
export function addressLabel(url) {
  return String(url || "").replace(/^https:\/\//i, "");
}

/** "Everyone" or "Workspace admins". */
export function audienceText(visibility) {
  return String(visibility || "").toLowerCase() === "admins" ? "Workspace admins" : "Everyone";
}

/** "Web · App", "Web", "App", or a dash when neither (the server refuses that, so it never sticks). */
export function showsOnText(portal) {
  const p = portal || {};
  const parts = [];
  if (p.showOnWeb) parts.push("Web");
  if (p.showOnApp) parts.push("App");
  return parts.length ? parts.join(" · ") : "—";
}

/**
 * "just now", "5 min ago", "2 h ago", "yesterday", "3 days ago", "on 3 Oct 2026"; "" for no time.
 * The API's instants carry no offset and are UTC (sourceTime.parseUtc).
 */
export function relativeTime(value, now) {
  const at = parseUtc(value);
  if (at == null) return "";
  const seconds = Math.max(0, Math.round(((now == null ? Date.now() : now) - at) / 1000));
  if (seconds < 45) return "just now";
  const minutes = Math.max(1, Math.round(seconds / 60));
  if (minutes < 60) return minutes + " min ago";
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return hours + " h ago";
  if (hours < 48) return "yesterday";
  const days = Math.floor(hours / 24);
  if (days < 7) return days + " days ago";
  const date = new Date(at);
  return "on " + date.getDate() + " " + MONTHS[date.getMonth()] + " " + date.getFullYear();
}

function twoDigits(n) {
  return (n < 10 ? "0" : "") + n;
}

/**
 * When a debug session ends, in this browser's time: "14:30" today, "tomorrow 09:15", else
 * "21 Oct 09:15"; "" for no time. The API's instants carry no offset and are UTC.
 */
export function untilText(value, now) {
  const at = parseUtc(value);
  if (at == null) return "";
  const date = new Date(at);
  const today = new Date(now == null ? Date.now() : now);
  const time = twoDigits(date.getHours()) + ":" + twoDigits(date.getMinutes());
  const dayOf = (d) => d.getFullYear() * 10000 + d.getMonth() * 100 + d.getDate();
  const tomorrow = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
  if (dayOf(date) === dayOf(today)) return time;
  if (dayOf(date) === dayOf(tomorrow)) return "tomorrow " + time;
  return date.getDate() + " " + MONTHS[date.getMonth()] + " " + time;
}

/** The port of a debug grant's origin ("http://localhost:5173" -> "5173"), or the origin itself. */
export function originPort(origin) {
  const match = /^https?:\/\/[^/:]+:(\d{1,5})\/?$/i.exec(String(origin || "").trim());
  return match ? match[1] : String(origin || "");
}

/** The Last opened cell: when, or "Never", then "· N open" while sessions are open. */
export function lastOpenedText(lastOpenedAt, openSessions, now) {
  const when = relativeTime(lastOpenedAt, now) || "Never";
  const open = Number(openSessions) || 0;
  return open > 0 ? when + " · " + open + " open" : when;
}

const ADDRESS_ERRORS = [
  "address_required",
  "address_too_long",
  "fragment_not_allowed",
  "path_not_allowed",
  "https_required",
  "origin_not_allowed",
  "path_required",
  "query_not_allowed",
  "page_unreachable",
  "not_a_portal",
];

/** Which form field a server refusal belongs under: "url", "name", "icon", "platforms", or null for the form. */
export function fieldOfError(code) {
  if (ADDRESS_ERRORS.indexOf(code) >= 0) return "url";
  if (code === "name_required" || code === "name_too_long") return "name";
  if (code === "icon_too_long") return "icon";
  if (code === "platform_required") return "platforms";
  return null;
}

/** The server's own words for a failed call, else the fallback. */
export function errorMessage(err, fallback) {
  const data = err && err.response && err.response.data;
  return (data && typeof data === "object" && (data.message || data.error_description)) || fallback;
}

/** The server's error code for a failed call, or "". */
export function errorCode(err) {
  const data = err && err.response && err.response.data;
  return (data && typeof data === "object" && typeof data.error === "string" && data.error) || "";
}

/**
 * The form's own checks before anything is sent; the server checks again, and owns the address
 * rules (they are answered by the Check, with its words). { name?, url?, platforms? } -> message.
 */
export function formProblems(form) {
  const f = form || {};
  const problems = {};
  const name = String(f.name || "").trim();
  if (!name) problems.name = "Give the portal a name.";
  else if (name.length > PORTAL_NAME_MAX) problems.name = "Keep the name to " + PORTAL_NAME_MAX + " characters.";
  if (!String(f.url || "").trim()) problems.url = "Enter the portal page's address.";
  if (!f.showOnWeb && !f.showOnApp) problems.platforms = "Show the portal on the web, the mobile app, or both.";
  return problems;
}

export default {
  PORTAL_NAME_MAX,
  PORTAL_ICON_MAX,
  addressHelp,
  addressLabel,
  audienceText,
  showsOnText,
  relativeTime,
  untilText,
  originPort,
  lastOpenedText,
  fieldOfError,
  errorMessage,
  errorCode,
  formProblems,
};
